import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Pagination,
  Grid,
  Stack,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import Main from '../layouts/Main';
import { useApi } from '../contexts/Api';
import ProductLogList from '../components/ProductLogList';

const SOURCE_LABELS = {
  sheet_push: 'Manual push',
  autosync:   'Auto-sync',
  cron:       'Cron',
  admin:      'Admin edit'
};

const ProductLogs = () => {
  const { productLogs, loadingProductLogs, fetchProductLogs } = useApi();
  const [filters, setFilters] = useState({ sku: '', from: '', to: '', source: '', action: '' });
  const [debouncedSku, setDebouncedSku] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSku(filters.sku);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [filters.sku]);

  useEffect(() => {
    fetchProductLogs({
      sku: debouncedSku,
      from: filters.from,
      to: filters.to,
      source: filters.source,
      action: filters.action
    }, page, 25);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSku, filters.from, filters.to, filters.source, filters.action, page]);

  const clearFilters = () => {
    setFilters({ sku: '', from: '', to: '', source: '', action: '' });
    setDebouncedSku('');
    setPage(1);
  };

  const hasFilters = filters.sku || filters.from || filters.to || filters.source || filters.action;

  return (
    <Main>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <HistoryIcon sx={{ fontSize: 24, mr: 1, verticalAlign: 'middle', color: '#845adf' }} />
            Product Update Logs
          </h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Product Logs</li>
          </ol>
        </div>
        <div className="page-header-actions">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchProductLogs({ sku: debouncedSku, ...filters }, page, 25)}
            sx={{ borderColor: '#845adf', color: '#845adf', '&:hover': { borderColor: '#6c48c5', backgroundColor: '#f3f0ff' } }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Box sx={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        p: 2.5,
        mb: 3
      }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search SKU"
              placeholder="e.g. 111890 or 1118*"
              value={filters.sku}
              onChange={(e) => setFilters(prev => ({ ...prev, sku: e.target.value }))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: filters.sku && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilters(prev => ({ ...prev, sku: '' }))}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              helperText="Exact or wildcard (use *)"
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              value={filters.from}
              onChange={(e) => { setFilters(prev => ({ ...prev, from: e.target.value })); setPage(1); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              value={filters.to}
              onChange={(e) => { setFilters(prev => ({ ...prev, to: e.target.value })); setPage(1); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={filters.source}
                label="Source"
                onChange={(e) => { setFilters(prev => ({ ...prev, source: e.target.value })); setPage(1); }}
              >
                <MenuItem value="">All</MenuItem>
                {(productLogs.sources || []).map(s => (
                  <MenuItem key={s} value={s}>{SOURCE_LABELS[s] || s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select
                value={filters.action}
                label="Action"
                onChange={(e) => { setFilters(prev => ({ ...prev, action: e.target.value })); setPage(1); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="inserted">Insert</MenuItem>
                <MenuItem value="updated">Update</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {hasFilters && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e5e7eb' }}>
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>ACTIVE FILTERS:</Typography>
            {filters.sku && <Chip size="small" label={`SKU: ${filters.sku}`} onDelete={() => setFilters(p => ({ ...p, sku: '' }))} />}
            {filters.from && <Chip size="small" label={`From: ${filters.from}`} onDelete={() => setFilters(p => ({ ...p, from: '' }))} />}
            {filters.to && <Chip size="small" label={`To: ${filters.to}`} onDelete={() => setFilters(p => ({ ...p, to: '' }))} />}
            {filters.source && <Chip size="small" label={`Source: ${SOURCE_LABELS[filters.source] || filters.source}`} onDelete={() => setFilters(p => ({ ...p, source: '' }))} />}
            {filters.action && <Chip size="small" label={`Action: ${filters.action}`} onDelete={() => setFilters(p => ({ ...p, action: '' }))} />}
            <Button size="small" onClick={clearFilters} sx={{ ml: 'auto', color: '#dc2626', textTransform: 'none' }}>
              Clear all
            </Button>
          </Stack>
        )}
      </Box>

      {/* Stats bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          <strong style={{ color: '#111827' }}>{productLogs.total.toLocaleString()}</strong> log{productLogs.total === 1 ? '' : 's'} found
          {hasFilters && ' (filtered)'}
        </Typography>
        {productLogs.pages > 1 && (
          <Typography variant="caption" sx={{ color: '#9ca3af' }}>
            Page {page} of {productLogs.pages}
          </Typography>
        )}
      </Box>

      {/* List */}
      {loadingProductLogs ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress sx={{ color: '#845adf' }} />
        </Box>
      ) : productLogs.logs.length === 0 ? (
        <Box sx={{
          textAlign: 'center',
          py: 8,
          backgroundColor: 'white',
          border: '1px dashed #d1d5db',
          borderRadius: 2
        }}>
          <HistoryIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
          <Typography variant="h6" sx={{ color: '#6b7280', mb: 0.5 }}>
            No logs found
          </Typography>
          <Typography variant="caption" sx={{ color: '#9ca3af' }}>
            {hasFilters ? 'Try adjusting your filters.' : 'Waiting for the first product update to arrive.'}
          </Typography>
        </Box>
      ) : (
        <ProductLogList logs={productLogs.logs} />
      )}

      {/* Pagination */}
      {productLogs.pages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={productLogs.pages}
            page={page}
            onChange={(_, p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Main>
  );
};

export default ProductLogs;
